import imagePath from "../../constants/imagePath";

export interface ServiceType {
  title: string;
  subtitle: string;
  description: string;
  image: string;
  number: string;
}

export interface StackItemsType {
  title: string;
  image: string;
  points: string[];
}

export interface IndustryType {
  title: string;
  banner: {
    title: string;
    highlight: string;
    description: string;
    cta: string;
    backgroundImage?: string;
  };
  services: {
    title: string;
    description: string;
    data: ServiceType[];
  };
  stackItems: {
    title: string;
    description: string;
    data: StackItemsType[];
  };
  readyToHire: {
    title: string;
    cta: string;
  };

  partnerReasons: {
    title: string;
    description: string;
    data: {
      title: string;
      description: string;
    }[];
  };
  process: {
    title: string;
    description: string;
    data: {
      title: string;
      description: string;
      image: string;
    }[];
  };
}

export const industriesData: Record<string, IndustryType> = {
  healthcare: {
    title: "HealthCare",
    banner: {
      title: "Transforming the Future of",
      highlight: "HealthCare",
      description:
        "We empower healthcare organizations with secure, scalable, and intelligent digital solutions that improve patient care, streamline clinical workflows, enhance data accuracy, ensure regulatory compliance, and support long-term digital transformation across the healthcare ecosystem.",
      cta: "Book a Free Consultant",
      backgroundImage: require("../../assets/images/health_care.webp"),
    },

    services: {
      title: "End-to-End Healthcare Technology Services We Offer.",
      description:
        "Our healthcare software development services help hospitals, clinics, laboratories, and health-tech companies automate operations, improve patient engagement, ensure data security, comply with healthcare regulations, and deliver efficient, technology-driven medical services across all care environments.",
      data: [
        {
          number: "01",
          title: "Electronic Health Records (EHR) Software Development",
          subtitle: "Electronic Health Records (EHR) Software Development",
          description:
            "We develop secure and interoperable EHR systems that enable healthcare providers to manage patient records, clinical histories, diagnostics, prescriptions, and treatment plans efficiently. Our solutions support real-time data access, seamless integrations, regulatory compliance, improved care coordination, and enhanced clinical decision-making.",
          image: imagePath.Industries,
        },
        {
          number: "02",
          title: "Hospital Management Software Development",
          subtitle: "Hospital Management Software Development",
          description:
            "Our hospital management systems streamline administrative and clinical workflows including appointment scheduling, patient admissions, billing, inventory, and staff management. These platforms reduce operational inefficiencies, improve resource utilization, enhance patient experiences, and provide real-time operational insights.",
          image: imagePath.NextSection,
        },
        {
          number: "03",
          title: "Telemedicine & Remote Care Solutions",
          subtitle: "Telemedicine & Remote Care Solutions",
          description:
            "We build telemedicine platforms that enable virtual consultations, remote patient monitoring, and secure communication between patients and healthcare providers. These solutions improve access to care, reduce hospital visits, ensure data privacy, and support continuous patient engagement across digital channels.",
          image: imagePath.Industries,
        },
        {
          number: "04",
          title: "Healthcare Analytics & Reporting Software",
          subtitle: "Healthcare Analytics & Reporting Software",
          description:
            "Our healthcare analytics solutions transform clinical and operational data into actionable insights. By leveraging data visualization, predictive analytics, and reporting tools, healthcare organizations can improve patient outcomes, optimize operations, reduce costs, and make informed data-driven decisions.",
          image: imagePath.NextSection,
        },
        {
          number: "05",
          title: "Medical Billing & Revenue Cycle Management Software",
          subtitle: "Medical Billing & Revenue Cycle Management Software",
          description:
            "We develop medical billing and revenue cycle management solutions that automate claims processing, coding, invoicing, and payment tracking. These systems reduce billing errors, improve cash flow, ensure compliance with healthcare regulations, and enhance overall financial performance.",
          image: imagePath.Industries,
        },
      ],
    },

    stackItems: {
      title: "Tailored Healthcare Software Solutions",
      description:
        "Our healthcare software solutions are customized to meet the unique needs of healthcare providers, ensuring scalability, interoperability, data security, regulatory compliance, and long-term adaptability while supporting complex clinical and administrative workflows.",
      data: [
        {
          title: "Clinical Data Management",
          image: imagePath.Tail,
          points: [
            "Secure storage of electronic health records",
            "Real-time access to patient clinical data",
            "Interoperability with healthcare systems and devices",
            "Advanced reporting and clinical analytics",
          ],
        },
        {
          title: "Healthcare Compliance & Security",
          image: imagePath.Tail,
          points: [
            "HIPAA and healthcare regulatory compliance",
            "Role-based access control and authentication",
            "End-to-end encryption for sensitive health data",
            "Continuous security monitoring and audits",
          ],
        },
      ],
    },

    readyToHire: {
      title: "Ready to Hire the Best Healthcare Software Developers",
      cta: "Hire our dev",
    },

    partnerReasons: {
      title: "Why Partner with Us",
      description:
        "We help healthcare organizations leverage technology to improve care delivery, enhance operational efficiency, ensure compliance, and build reliable digital healthcare solutions.",
      data: [
        {
          title: "Healthcare Domain Expertise",
          description:
            "Extensive experience in hospital systems, telemedicine, health analytics, and medical software solutions.",
        },
        {
          title: "Security & Compliance Focus",
          description:
            "Strict adherence to healthcare regulations and data protection standards.",
        },
        {
          title: "Scalable & Interoperable Solutions",
          description:
            "Future-ready systems designed to integrate seamlessly with existing healthcare infrastructure.",
        },
        {
          title: "Dedicated Support & Maintenance",
          description:
            "Ongoing technical support, updates, and system optimization.",
        },
      ],
    },

    process: {
      title: "Our Process for Healthcare Software Development",
      description:
        "A structured and patient-centric development approach focused on security, compliance, usability, and performance for healthcare software solutions.",
      data: [
        {
          title: "Healthcare Consultation & Strategy",
          description:
            "Understanding clinical workflows, patient needs, and regulatory requirements to define the right technology strategy.",
          image: imagePath.Img1,
        },
        {
          title: "Requirement Analysis & Planning",
          description:
            "Defining system features, integrations, data standards, and compliance needs in detail.",
          image: imagePath.Img2,
        },
        {
          title: "UI/UX Design",
          description:
            "Designing intuitive, accessible, and user-friendly healthcare interfaces for patients and providers.",
          image: imagePath.Img3,
        },
        {
          title: "Development & System Integration",
          description:
            "Building secure healthcare software with third-party integrations and medical device compatibility.",
          image: imagePath.Img4,
        },
        {
          title: "Quality Assurance & Compliance Testing",
          description:
            "Thorough testing to ensure performance, security, usability, and regulatory compliance.",
          image: imagePath.Img5,
        },
        {
          title: "Deployment & Continuous Enhancement",
          description:
            "Smooth deployment with continuous monitoring, updates, and feature improvements.",
          image: imagePath.Img6,
        },
      ],
    },
  },
  education: {
    title: "Education",
    banner: {
      title: "Shaping the Digital Future of",
      highlight: "Education",
      description:
        "We help educational institutions, training organizations, and ed-tech companies build powerful digital learning solutions that enhance student engagement, streamline academic operations, support remote education, and enable scalable, technology-driven learning experiences across modern education ecosystems.",
      cta: "Book a Free Consultant",
      backgroundImage: require("../../assets/images/education.webp"),
    },

    services: {
      title: "End-to-End Education Technology Services We Offer.",
      description:
        "Our education software development services empower schools, universities, and ed-tech platforms to modernize learning environments, automate administrative processes, improve collaboration, enhance learner outcomes, and deliver secure, flexible, and accessible digital education solutions.",
      data: [
        {
          number: "01",
          title: "Learning Management System (LMS) Development",
          subtitle: "Learning Management System (LMS) Development",
          description:
            "We design and develop feature-rich learning management systems that enable institutions to create, manage, and deliver digital courses effectively. Our LMS solutions support virtual classrooms, content management, assessments, progress tracking, analytics, and integrations to enhance teaching efficiency and student engagement.",
          image: imagePath.Industries,
        },
        {
          number: "02",
          title: "School & University Management Software",
          subtitle: "School & University Management Software",
          description:
            "Our academic management software streamlines administrative operations such as admissions, enrollment, attendance, scheduling, examinations, and fee management. These platforms reduce manual workloads, improve data accuracy, enhance communication, and provide real-time insights for better institutional decision-making.",
          image: imagePath.NextSection,
        },
        {
          number: "03",
          title: "Virtual Classroom & E-Learning Solutions",
          subtitle: "Virtual Classroom & E-Learning Solutions",
          description:
            "We build interactive virtual classroom and e-learning platforms that support live sessions, recorded lectures, discussions, quizzes, and collaboration tools. These solutions enable remote learning, improve accessibility, encourage learner participation, and ensure consistent education delivery across digital channels.",
          image: imagePath.Industries,
        },
        {
          number: "04",
          title: "Education Analytics & Performance Tracking",
          subtitle: "Education Analytics & Performance Tracking",
          description:
            "Our education analytics solutions help institutions track student performance, engagement, and learning outcomes using data-driven insights. With advanced reporting and visualization tools, educators can identify learning gaps, personalize instruction, and continuously improve academic performance and curriculum effectiveness.",
          image: imagePath.NextSection,
        },
        {
          number: "05",
          title: "Assessment & Examination Management Software",
          subtitle: "Assessment & Examination Management Software",
          description:
            "We develop secure assessment and examination management systems that support online tests, grading automation, result processing, and performance analysis. These solutions improve evaluation accuracy, reduce administrative effort, maintain academic integrity, and enhance the overall assessment experience.",
          image: imagePath.Industries,
        },
      ],
    },

    stackItems: {
      title: "Tailored Education Software Solutions",
      description:
        "Our education software solutions are carefully tailored to meet the diverse needs of academic institutions and ed-tech platforms, ensuring scalability, data security, accessibility, seamless integrations, and long-term adaptability for evolving digital learning environments.",
      data: [
        {
          title: "Learning & Student Data Management",
          image: imagePath.Tail,
          points: [
            "Centralized storage of student and academic records",
            "Real-time tracking of learner progress and performance",
            "Integration with third-party learning tools and platforms",
            "Advanced reporting and academic analytics capabilities",
          ],
        },
        {
          title: "Digital Learning Security & Compliance",
          image: imagePath.Tail,
          points: [
            "Secure access control for students and educators",
            "Data privacy and protection for learner information",
            "Compliance with education standards and regulations",
            "Continuous monitoring and system security updates",
          ],
        },
      ],
    },

    readyToHire: {
      title: "Ready to Hire the Best Education Software Developers",
      cta: "Hire our dev",
    },

    partnerReasons: {
      title: "Why Partner with Us",
      description:
        "We combine education domain expertise with modern technologies to deliver reliable, scalable, and engaging digital learning solutions that support academic excellence and institutional growth.",
      data: [
        {
          title: "Education Domain Expertise",
          description:
            "Proven experience in LMS platforms, virtual learning systems, and academic management software.",
        },
        {
          title: "User-Centric Design Approach",
          description:
            "Intuitive interfaces designed for students, educators, and administrators.",
        },
        {
          title: "Scalable & Future-Ready Platforms",
          description:
            "Solutions built to support growing learners and evolving education models.",
        },
        {
          title: "Dedicated Support & Maintenance",
          description:
            "Ongoing technical support, performance monitoring, and feature enhancements.",
        },
      ],
    },

    process: {
      title: "Our Process for Education Software Development",
      description:
        "A structured and learner-focused development process that ensures usability, scalability, security, and performance for education technology solutions.",
      data: [
        {
          title: "Education Consultation & Strategy",
          description:
            "Understanding learning objectives, user needs, and academic workflows to define the right technology approach.",
          image: imagePath.Img1,
        },
        {
          title: "Requirement Analysis & Planning",
          description:
            "Detailed analysis of system features, integrations, scalability, and compliance requirements.",
          image: imagePath.Img2,
        },
        {
          title: "UI/UX Design",
          description:
            "Designing intuitive, accessible, and engaging learning interfaces for all users.",
          image: imagePath.Img3,
        },
        {
          title: "Development & Integration",
          description:
            "Building robust education software with third-party tools and platform integrations.",
          image: imagePath.Img4,
        },
        {
          title: "Quality Assurance & Testing",
          description:
            "Comprehensive testing to ensure performance, usability, security, and reliability.",
          image: imagePath.Img5,
        },
        {
          title: "Deployment & Continuous Improvement",
          description:
            "Smooth deployment with continuous enhancements based on user feedback and analytics.",
          image: imagePath.Img6,
        },
      ],
    },
  },
  sports: {
    title: "Sports",
    banner: {
      title: "Powering the Digital Evolution of",
      highlight: "Sports",
      description:
        "We help sports organizations, leagues, clubs, and fitness brands leverage innovative digital solutions that enhance athlete performance, improve fan engagement, streamline operations, enable data-driven decisions, and support scalable growth across the modern sports ecosystem.",
      cta: "Book a Free Consultant",
      backgroundImage: require("../../assets/images/sports.webp"),
    },

    services: {
      title: "End-to-End Sports Technology Services We Offer.",
      description:
        "Our sports software development services are designed to support teams, academies, federations, and sports-tech companies by automating operations, improving performance analytics, enhancing fan experiences, enabling digital engagement, and delivering secure, scalable technology platforms.",
      data: [
        {
          number: "01",
          title: "Sports Management Software Development",
          subtitle: "Sports Management Software Development",
          description:
            "We build comprehensive sports management platforms that help organizations manage teams, schedules, events, memberships, facilities, and finances efficiently. These solutions reduce administrative workload, improve coordination, enhance operational visibility, and provide centralized control over sports activities and resources.",
          image: imagePath.Industries,
        },
        {
          number: "02",
          title: "Athlete Performance Tracking Solutions",
          subtitle: "Athlete Performance Tracking Solutions",
          description:
            "Our athlete performance tracking systems collect and analyze data related to training, fitness, health, and performance metrics. By using analytics and reporting tools, coaches and trainers can monitor progress, prevent injuries, optimize training programs, and improve overall athletic performance.",
          image: imagePath.NextSection,
        },
        {
          number: "03",
          title: "Fan Engagement & Sports Mobile Apps",
          subtitle: "Fan Engagement & Sports Mobile Apps",
          description:
            "We develop fan engagement platforms and mobile applications that deliver live updates, match statistics, ticketing, merchandise, and interactive content. These solutions strengthen fan loyalty, improve digital experiences, increase audience reach, and create new revenue opportunities for sports brands.",
          image: imagePath.Industries,
        },
        {
          number: "04",
          title: "Sports Analytics & Data Visualization",
          subtitle: "Sports Analytics & Data Visualization",
          description:
            "Our sports analytics solutions transform performance, match, and operational data into actionable insights. With advanced dashboards and visualization tools, teams and organizations can analyze trends, evaluate strategies, improve decision-making, and gain a competitive edge through data-driven intelligence.",
          image: imagePath.NextSection,
        },
        {
          number: "05",
          title: "Fitness & Wellness Application Development",
          subtitle: "Fitness & Wellness Application Development",
          description:
            "We create fitness and wellness applications that support workout tracking, training plans, nutrition guidance, and progress monitoring. These platforms help users stay motivated, improve health outcomes, enable personalized fitness journeys, and support engagement across digital fitness ecosystems.",
          image: imagePath.Industries,
        },
      ],
    },

    stackItems: {
      title: "Tailored Sports Software Solutions",
      description:
        "Our sports software solutions are tailored to meet the diverse needs of sports organizations and fitness brands, ensuring scalability, data accuracy, system integration, user engagement, and long-term adaptability in a rapidly evolving digital sports environment.",
      data: [
        {
          title: "Performance & Sports Data Management",
          image: imagePath.Tail,
          points: [
            "Centralized storage of athlete and performance data",
            "Real-time tracking and monitoring of training metrics",
            "Integration with wearable devices and sensors",
            "Advanced analytics and performance reporting tools",
          ],
        },
        {
          title: "Fan Engagement & Platform Security",
          image: imagePath.Tail,
          points: [
            "Secure user authentication and access management",
            "Data protection for fan and athlete information",
            "Scalable infrastructure for high user traffic",
            "Continuous monitoring and platform optimization",
          ],
        },
      ],
    },

    readyToHire: {
      title: "Ready to Hire the Best Sports Software Developers",
      cta: "Hire our dev",
    },

    partnerReasons: {
      title: "Why Partner with Us",
      description:
        "We help sports organizations adopt modern technologies that improve performance, enhance fan engagement, streamline operations, and support sustainable digital growth.",
      data: [
        {
          title: "Sports Domain Expertise",
          description:
            "Hands-on experience with sports management, analytics, and fitness technology solutions.",
        },
        {
          title: "User-Focused Digital Experiences",
          description:
            "Engaging platforms designed for athletes, coaches, and sports fans.",
        },
        {
          title: "Scalable & High-Performance Systems",
          description:
            "Solutions built to handle growing data volumes and user bases.",
        },
        {
          title: "Reliable Support & Maintenance",
          description:
            "Continuous support, system monitoring, and feature enhancements.",
        },
      ],
    },

    process: {
      title: "Our Process for Sports Software Development",
      description:
        "A structured and performance-driven development process focused on usability, scalability, security, and innovation for sports technology solutions.",
      data: [
        {
          title: "Sports Consultation & Strategy",
          description:
            "Understanding sports operations, performance goals, and user needs to define the right digital strategy.",
          image: imagePath.Img1,
        },
        {
          title: "Requirement Analysis & Planning",
          description:
            "Detailed analysis of features, integrations, scalability, and data requirements.",
          image: imagePath.Img2,
        },
        {
          title: "UI/UX Design",
          description:
            "Designing engaging and intuitive interfaces for athletes, staff, and fans.",
          image: imagePath.Img3,
        },
        {
          title: "Development & Integration",
          description:
            "Building robust sports platforms with third-party integrations and analytics tools.",
          image: imagePath.Img4,
        },
        {
          title: "Quality Assurance & Testing",
          description:
            "Extensive testing to ensure performance, security, and reliability.",
          image: imagePath.Img5,
        },
        {
          title: "Deployment & Continuous Improvement",
          description:
            "Smooth deployment with ongoing enhancements and performance optimization.",
          image: imagePath.Img6,
        },
      ],
    },
  },
  "finance-banking": {
    title: "Finance & Banking",
    banner: {
      title: "Driving Digital Innovation in",
      highlight: "Finance & Banking",
      description:
        "We help banks, financial institutions, and fintech organizations modernize their operations through secure, scalable, and intelligent digital solutions that improve customer experiences, strengthen compliance, enhance operational efficiency, and enable long-term growth in an evolving financial landscape.",
      cta: "Book a Free Cons",
      backgroundImage: require("../../assets/images/finance-banking.webp"),
    },

    services: {
      title: "End-to-End Finance & Banking Technology Services We Offer.",
      description:
        "Our finance and banking software development services support traditional banks, digital banks, and fintech companies by automating processes, strengthening security, ensuring regulatory compliance, improving customer engagement, and delivering reliable, future-ready financial technology solutions.",
      data: [
        {
          number: "01",
          title: "Core Banking Software Development",
          subtitle: "Core Banking Software Development",
          description:
            "We develop robust core banking systems that enable banks to manage customer accounts, transactions, loans, deposits, and branch operations efficiently. Our solutions support real-time processing, multi-channel access, system integrations, regulatory compliance, scalability, and seamless digital banking experiences.",
          image: imagePath.Industries,
        },
        {
          number: "02",
          title: "Digital Banking & Mobile Banking Solutions",
          subtitle: "Digital Banking & Mobile Banking Solutions",
          description:
            "Our digital and mobile banking platforms provide customers with secure access to accounts, payments, transfers, and financial services anytime, anywhere. These solutions enhance user experience, improve customer engagement, reduce operational costs, and support omnichannel banking strategies.",
          image: imagePath.NextSection,
        },
        {
          number: "03",
          title: "Payment Processing & Wallet Solutions",
          subtitle: "Payment Processing & Wallet Solutions",
          description:
            "We build secure payment processing systems and digital wallets that support online payments, mobile transactions, recurring billing, and third-party integrations. Our solutions include fraud prevention, encryption, compliance support, and high availability to ensure fast and reliable transactions.",
          image: imagePath.Industries,
        },
        {
          number: "04",
          title: "Risk, Fraud & Compliance Management Software",
          subtitle: "Risk, Fraud & Compliance Management Software",
          description:
            "Our risk and compliance solutions help financial institutions manage AML, KYC, fraud detection, and regulatory reporting requirements. By leveraging automation and analytics, these systems reduce operational risks, improve monitoring, ensure compliance, and enhance regulatory transparency.",
          image: imagePath.NextSection,
        },
        {
          number: "05",
          title: "Wealth & Investment Management Software",
          subtitle: "Wealth & Investment Management Software",
          description:
            "We develop wealth and investment management platforms that support portfolio management, trading, asset allocation, performance analytics, and client reporting. These solutions empower advisors and investors with data-driven insights, automation, transparency, and personalized financial planning tools.",
          image: imagePath.Industries,
        },
      ],
    },

    stackItems: {
      title: "Tailored Finance & Banking Software Solutions",
      description:
        "Our finance and banking software solutions are tailored to meet the complex needs of financial institutions, ensuring scalability, high availability, data security, regulatory compliance, and long-term adaptability across rapidly evolving financial ecosystems.",
      data: [
        {
          title: "Financial Data & Transaction Management",
          image: imagePath.Tail,
          points: [
            "Real-time processing of financial transactions",
            "Secure storage of sensitive banking data",
            "High-performance systems for large transaction volumes",
            "Advanced reporting and financial analytics",
          ],
        },
        {
          title: "Security, Risk & Compliance Frameworks",
          image: imagePath.Tail,
          points: [
            "AML and KYC compliance implementation",
            "Role-based access control and authentication",
            "End-to-end data encryption and monitoring",
            "Regular audits and regulatory reporting support",
          ],
        },
      ],
    },

    readyToHire: {
      title: "Ready to Hire the Best Finance & Banking Software Developers",
      cta: "Hire our dev",
    },

    partnerReasons: {
      title: "Why Partner with Us",
      description:
        "We combine deep finance and banking domain expertise with modern technologies to deliver secure, scalable, and compliant software solutions that drive digital transformation.",
      data: [
        {
          title: "Banking & Finance Domain Expertise",
          description:
            "Extensive experience across core banking, payments, digital banking, and fintech solutions.",
        },
        {
          title: "Security & Compliance Focus",
          description:
            "Strict adherence to financial regulations, security standards, and data protection practices.",
        },
        {
          title: "Scalable & Future-Ready Architecture",
          description:
            "Solutions designed to grow with transaction volumes and customer demand.",
        },
        {
          title: "Dedicated Support & Maintenance",
          description:
            "Ongoing support, monitoring, and continuous system improvements.",
        },
      ],
    },

    process: {
      title: "Our Process for Finance & Banking Software Development",
      description:
        "A structured and security-focused development process that ensures performance, compliance, scalability, and reliability for finance and banking technology solutions.",
      data: [
        {
          title: "Finance & Banking Consultation",
          description:
            "Understanding business objectives, regulatory requirements, and banking workflows to define a clear technology strategy.",
          image: imagePath.Img1,
        },
        {
          title: "Requirement Analysis & Planning",
          description:
            "Detailed planning of features, integrations, risk controls, and compliance needs.",
          image: imagePath.Img2,
        },
        {
          title: "UI/UX Design",
          description:
            "Designing intuitive, secure, and customer-friendly banking interfaces.",
          image: imagePath.Img3,
        },
        {
          title: "Development & System Integration",
          description:
            "Building robust banking platforms with secure APIs and third-party integrations.",
          image: imagePath.Img4,
        },
        {
          title: "Quality Assurance & Security Testing",
          description:
            "Extensive testing to ensure system performance, data security, and regulatory compliance.",
          image: imagePath.Img5,
        },
        {
          title: "Deployment & Continuous Optimization",
          description:
            "Smooth deployment with ongoing monitoring, updates, and performance enhancements.",
          image: imagePath.Img6,
        },
      ],
    },
  },

  logistics: {
    title: "Logistics",
    banner: {
      title: "Driving Digital Transformation in",
      highlight: "Logistics",
      description:
        "We help logistics and transportation companies modernize their operations through intelligent, scalable, and data-driven digital solutions that improve supply chain visibility, optimize transportation processes, reduce operational costs, enhance delivery performance, and support long-term business growth.",
      cta: "Book a Free Cons",
      backgroundImage: require("../../assets/images/logistics.webp"),
    },

    services: {
      title: "End-to-End Logistics Technology Services We Offer.",
      description:
        "Our logistics software development services support logistics providers, transportation companies, and supply chain enterprises by automating workflows, improving real-time visibility, enhancing coordination, optimizing resource utilization, and delivering secure, scalable technology solutions for complex logistics operations.",
      data: [
        {
          number: "01",
          title: "Shipping & Logistics Management Software Development",
          subtitle: "Shipping & Logistics Management Software Development",
          description:
            "We develop comprehensive shipping and logistics management software that enables businesses to manage shipments, warehouse operations, billing, documentation, and multi-location logistics workflows efficiently. These solutions improve shipment tracking, reduce manual processes, enhance coordination, and provide real-time insights across the entire supply chain.",
          image: imagePath.Industries,
        },
        {
          number: "02",
          title: "Fleet Management Software Development",
          subtitle: "Fleet Management Software Development",
          description:
            "Our fleet management solutions help logistics companies monitor vehicles, drivers, fuel usage, maintenance schedules, and compliance in real time. By leveraging GPS tracking and analytics, these systems improve fleet efficiency, reduce operational costs, enhance safety, and support data-driven transportation decisions.",
          image: imagePath.NextSection,
        },
        {
          number: "03",
          title: "Telematics Software Development",
          subtitle: "Telematics Software Development",
          description:
            "We build advanced telematics software that collects and analyzes vehicle data including location, speed, fuel consumption, and diagnostics. These solutions enable predictive maintenance, improve driver behavior monitoring, enhance operational visibility, and support smarter fleet and transportation management strategies.",
          image: imagePath.Industries,
        },
        {
          number: "04",
          title: "Asset Tracking Software Development",
          subtitle: "Asset Tracking Software Development",
          description:
            "Our asset tracking solutions use RFID, GPS, and IoT technologies to track and monitor logistics assets in real time. These systems improve asset utilization, reduce losses, enhance inventory accuracy, and provide complete visibility across warehouses, transportation routes, and supply chain networks.",
          image: imagePath.NextSection,
        },
        {
          number: "05",
          title: "Transportation Management Software Development",
          subtitle: "Transportation Management Software Development",
          description:
            "We develop transportation management systems that support route planning, carrier management, shipment optimization, and cost control. These solutions improve delivery efficiency, reduce transportation expenses, enhance customer satisfaction, and provide end-to-end visibility into transportation operations.",
          image: imagePath.Industries,
        },
      ],
    },

    stackItems: {
      title: "Tailored Logistics Software Solutions",
      description:
        "Our logistics software solutions are tailored to meet the unique needs of logistics and transportation businesses, ensuring scalability, real-time visibility, seamless integrations, operational efficiency, and long-term adaptability across evolving supply chain environments.",
      data: [
        {
          title: "Supply Chain & Operations Management",
          image: imagePath.Tail,
          points: [
            "Real-time tracking of shipments and logistics operations",
            "Centralized management of warehouses and inventories",
            "Integration with third-party logistics and ERP systems",
            "Advanced reporting and operational analytics",
          ],
        },
        {
          title: "Fleet, Asset & Security Management",
          image: imagePath.Tail,
          points: [
            "Vehicle and asset monitoring using GPS and IoT",
            "Role-based access control and system security",
            "Data protection for operational and customer information",
            "Continuous monitoring and system performance optimization",
          ],
        },
      ],
    },

    readyToHire: {
      title: "Ready to Hire the Best Logistics Software Developers",
      cta: "Hire our dev",
    },

    partnerReasons: {
      title: "Why Partner with Us",
      description:
        "We combine logistics domain expertise, modern technologies, and proven development practices to deliver reliable, scalable, and performance-driven logistics software solutions.",
      data: [
        {
          title: "Logistics Domain Expertise",
          description:
            "Extensive experience in logistics, transportation, and supply chain software development.",
        },
        {
          title: "Data-Driven & Scalable Solutions",
          description:
            "Platforms designed to handle growing shipment volumes and complex logistics workflows.",
        },
        {
          title: "Operational Efficiency Focus",
          description:
            "Solutions built to reduce costs, improve visibility, and enhance delivery performance.",
        },
        {
          title: "Dedicated Support & Maintenance",
          description:
            "Ongoing technical support, monitoring, and continuous system enhancements.",
        },
      ],
    },

    process: {
      title: "Our Process for Logistics Software Development",
      description:
        "A structured and results-driven development process focused on scalability, performance, security, and operational efficiency for logistics technology solutions.",
      data: [
        {
          title: "Logistics Consultation & Strategy",
          description:
            "Understanding logistics workflows, business goals, and operational challenges to define the right digital strategy.",
          image: imagePath.Img1,
        },
        {
          title: "Requirement Analysis & Planning",
          description:
            "Detailed planning of features, integrations, scalability, and system architecture.",
          image: imagePath.Img2,
        },
        {
          title: "UI/UX Design",
          description:
            "Designing intuitive and user-friendly interfaces for logistics teams and stakeholders.",
          image: imagePath.Img3,
        },
        {
          title: "Development & Integration",
          description:
            "Building robust logistics platforms with seamless third-party and system integrations.",
          image: imagePath.Img4,
        },
        {
          title: "Quality Assurance & Testing",
          description:
            "Extensive testing to ensure performance, reliability, and data accuracy.",
          image: imagePath.Img5,
        },
        {
          title: "Deployment & Continuous Optimization",
          description:
            "Smooth deployment with continuous monitoring, updates, and performance improvements.",
          image: imagePath.Img6,
        },
      ],
    },
  },
  "on-demand": {
    title: "On-Demand",
    banner: {
      title: "Powering the Future of",
      highlight: "On-Demand Services",
      description:
        "We help on-demand startups and enterprises build scalable, high-performance digital platforms that enable real-time service delivery, seamless user experiences, efficient operations, and rapid market expansion across diverse on-demand business models.",
      cta: "Book a Free Cons",
      backgroundImage: require("../../assets/images/on_demand.webp"),
    },

    services: {
      title: "End-to-End On-Demand Application Development Services We Offer.",
      description:
        "Our on-demand software development services support businesses across multiple industries by building feature-rich platforms that automate service workflows, enable real-time interactions, enhance customer satisfaction, ensure data security, and deliver fast, reliable, and scalable on-demand digital solutions.",
      data: [
        {
          number: "01",
          title: "On-Demand Mobile App Development",
          subtitle: "On-Demand Mobile App Development",
          description:
            "We develop robust on-demand mobile applications that connect customers with service providers in real time. Our solutions include user registration, service discovery, booking, real-time tracking, in-app communication, secure payments, and ratings, helping businesses deliver seamless and responsive on-demand experiences.",
          image: imagePath.Industries,
        },
        {
          number: "02",
          title: "On-Demand Web Platform Development",
          subtitle: "On-Demand Web Platform Development",
          description:
            "Our on-demand web platforms are designed to manage service requests, provider onboarding, order tracking, and customer interactions efficiently. These platforms improve operational transparency, support high user traffic, enable real-time updates, and provide centralized control over on-demand service operations.",
          image: imagePath.NextSection,
        },
        {
          number: "03",
          title: "Multi-Vendor Marketplace Solutions",
          subtitle: "Multi-Vendor Marketplace Solutions",
          description:
            "We build multi-vendor marketplace solutions that allow multiple service providers to offer services through a single platform. These systems support vendor management, service listings, pricing control, order assignment, commission tracking, and analytics to scale on-demand business ecosystems.",
          image: imagePath.Industries,
        },
        {
          number: "04",
          title: "Real-Time Tracking & Dispatch Systems",
          subtitle: "Real-Time Tracking & Dispatch Systems",
          description:
            "Our real-time tracking and dispatch systems enable businesses to assign service requests dynamically and monitor provider locations in real time. These solutions improve service response times, optimize resource utilization, enhance customer transparency, and support efficient on-demand service fulfillment.",
          image: imagePath.NextSection,
        },
        {
          number: "05",
          title: "Payment, Billing & Subscription Solutions",
          subtitle: "Payment, Billing & Subscription Solutions",
          description:
            "We develop secure payment, billing, and subscription management solutions for on-demand platforms. These systems support multiple payment methods, automated invoicing, commission management, recurring subscriptions, and financial reporting to ensure smooth and reliable monetization processes.",
          image: imagePath.Industries,
        },
      ],
    },

    stackItems: {
      title: "Tailored On-Demand Software Solutions",
      description:
        "Our on-demand software solutions are tailored to meet the unique requirements of fast-growing on-demand businesses, ensuring scalability, real-time performance, seamless integrations, operational efficiency, and long-term adaptability in competitive digital marketplaces.",
      data: [
        {
          title: "On-Demand Platform Management",
          image: imagePath.Tail,
          points: [
            "Real-time service request and order management",
            "Provider onboarding and availability tracking",
            "Automated service assignment and dispatching",
            "Advanced reporting and operational analytics",
          ],
        },
        {
          title: "Security, Payments & Scalability",
          image: imagePath.Tail,
          points: [
            "Secure user authentication and access control",
            "Encrypted payment processing and billing systems",
            "Scalable infrastructure for high user traffic",
            "Continuous monitoring and platform optimization",
          ],
        },
      ],
    },

    readyToHire: {
      title: "Ready to Hire the Best On-Demand App Developers",
      cta: "Hire our dev",
    },

    partnerReasons: {
      title: "Why Partner with Us",
      description:
        "We help on-demand businesses launch, scale, and optimize digital platforms by combining domain expertise, modern technologies, and agile development practices.",
      data: [
        {
          title: "On-Demand Domain Expertise",
          description:
            "Proven experience in building on-demand platforms across multiple service-based industries.",
        },
        {
          title: "Scalable & High-Performance Solutions",
          description:
            "Platforms designed to handle rapid growth and real-time service demands.",
        },
        {
          title: "User-Centric Design Approach",
          description:
            "Intuitive experiences for customers, service providers, and administrators.",
        },
        {
          title: "Reliable Support & Maintenance",
          description:
            "Ongoing support, monitoring, and continuous platform enhancements.",
        },
      ],
    },

    process: {
      title: "Our Process for On-Demand Software Development",
      description:
        "A structured and agile development process focused on speed, scalability, performance, and user experience for on-demand digital platforms.",
      data: [
        {
          title: "On-Demand Business Consultation",
          description:
            "Understanding business goals, service workflows, and user expectations to define the right on-demand strategy.",
          image: imagePath.Img1,
        },
        {
          title: "Requirement Analysis & Planning",
          description:
            "Detailed planning of platform features, integrations, scalability, and monetization models.",
          image: imagePath.Img2,
        },
        {
          title: "UI/UX Design",
          description:
            "Designing intuitive and responsive interfaces for on-demand users and providers.",
          image: imagePath.Img3,
        },
        {
          title: "Development & Integration",
          description:
            "Building robust on-demand platforms with real-time features and third-party integrations.",
          image: imagePath.Img4,
        },
        {
          title: "Quality Assurance & Testing",
          description:
            "Extensive testing to ensure performance, security, and reliability under real-world usage.",
          image: imagePath.Img5,
        },
        {
          title: "Deployment & Continuous Scaling",
          description:
            "Smooth deployment with continuous optimization and scalability enhancements.",
          image: imagePath.Img6,
        },
      ],
    },
  },
  "travel-hospitality": {
    title: "Travel",
    banner: {
      title: "Transforming Digital Experiences in",
      highlight: "Travel",
      description:
        "We help travel businesses, agencies, tour operators, and hospitality providers build intelligent, scalable, and customer-centric digital solutions that enhance traveler experiences, streamline operations, enable real-time bookings, and support sustainable growth across the global travel ecosystem.",
      cta: "Book a Free Cons",
      backgroundImage: require("../../assets/images/travel.webp"),
    },

    services: {
      title: "End-to-End Travel Technology Services We Offer.",
      description:
        "Our travel software development services empower travel companies to automate booking operations, improve customer engagement, integrate multiple travel services, enhance data visibility, and deliver seamless, secure, and personalized digital travel experiences across web and mobile platforms.",
      data: [
        {
          number: "01",
          title: "Travel Booking Engine Development",
          subtitle: "Travel Booking Engine Development",
          description:
            "We develop advanced travel booking engines that enable customers to search, compare, and book flights, hotels, packages, and activities in real time. Our solutions support dynamic pricing, availability management, secure payments, third-party API integrations, and seamless booking experiences across multiple channels.",
          image: imagePath.Industries,
        },
        {
          number: "02",
          title: "Travel Management Software Development",
          subtitle: "Travel Management Software Development",
          description:
            "Our travel management software helps agencies and enterprises manage itineraries, reservations, suppliers, invoices, and customer data efficiently. These platforms reduce manual work, improve operational visibility, enhance coordination, and provide centralized control over complex travel operations.",
          image: imagePath.NextSection,
        },
        {
          number: "03",
          title: "Tour & Package Management Solutions",
          subtitle: "Tour & Package Management Solutions",
          description:
            "We build tour and package management systems that allow travel businesses to create, manage, and customize travel packages easily. These solutions support pricing control, availability tracking, booking management, promotions, and analytics to improve package sales and customer satisfaction.",
          image: imagePath.Industries,
        },
        {
          number: "04",
          title: "Travel CRM & Customer Engagement Platforms",
          subtitle: "Travel CRM & Customer Engagement Platforms",
          description:
            "Our travel CRM solutions help businesses manage customer interactions, preferences, and communication across the travel journey. By leveraging automation and analytics, these platforms improve customer retention, personalize travel experiences, enhance marketing efforts, and strengthen long-term traveler relationships.",
          image: imagePath.NextSection,
        },
        {
          number: "05",
          title: "Travel Analytics & Reporting Solutions",
          subtitle: "Travel Analytics & Reporting Solutions",
          description:
            "We develop travel analytics and reporting solutions that transform booking, customer, and operational data into actionable insights. These tools help travel businesses optimize pricing strategies, forecast demand, improve decision-making, reduce costs, and enhance overall business performance.",
          image: imagePath.Industries,
        },
      ],
    },

    stackItems: {
      title: "Tailored Travel Software Solutions",
      description:
        "Our travel software solutions are tailored to meet the unique needs of travel and hospitality businesses, ensuring scalability, real-time performance, seamless integrations, data security, and long-term adaptability in a fast-evolving digital travel landscape.",
      data: [
        {
          title: "Booking & Travel Operations Management",
          image: imagePath.Tail,
          points: [
            "Real-time booking and reservation management",
            "Centralized control of travel services and inventory",
            "Integration with third-party travel APIs and systems",
            "Advanced reporting and operational analytics",
          ],
        },
        {
          title: "Security, Payments & Scalability",
          image: imagePath.Tail,
          points: [
            "Secure user authentication and access management",
            "Encrypted payment processing and transaction handling",
            "Scalable infrastructure for seasonal traffic spikes",
            "Continuous monitoring and system performance optimization",
          ],
        },
      ],
    },

    readyToHire: {
      title: "Ready to Hire the Best Travel Software Developers",
      cta: "Hire our dev",
    },

    partnerReasons: {
      title: "Why Partner with Us",
      description:
        "We help travel businesses accelerate digital transformation by combining travel domain expertise, modern technologies, and proven development practices.",
      data: [
        {
          title: "Travel Domain Expertise",
          description:
            "Strong experience in travel booking systems, CRM platforms, and travel management solutions.",
        },
        {
          title: "Customer-Centric Digital Experiences",
          description:
            "Platforms designed to deliver seamless and personalized traveler journeys.",
        },
        {
          title: "Scalable & Reliable Solutions",
          description:
            "Systems built to support high traffic volumes and global travel operations.",
        },
        {
          title: "Dedicated Support & Maintenance",
          description:
            "Ongoing technical support, monitoring, and continuous improvements.",
        },
      ],
    },

    process: {
      title: "Our Process for Travel Software Development",
      description:
        "A structured and agile development process focused on usability, scalability, performance, and reliability for travel technology solutions.",
      data: [
        {
          title: "Travel Consultation & Strategy",
          description:
            "Understanding travel business models, customer journeys, and operational challenges to define the right digital strategy.",
          image: imagePath.Img1,
        },
        {
          title: "Requirement Analysis & Planning",
          description:
            "Detailed planning of features, integrations, scalability, and technology architecture.",
          image: imagePath.Img2,
        },
        {
          title: "UI/UX Design",
          description:
            "Designing intuitive and engaging travel experiences for customers and administrators.",
          image: imagePath.Img3,
        },
        {
          title: "Development & Integration",
          description:
            "Building robust travel platforms with third-party API and system integrations.",
          image: imagePath.Img4,
        },
        {
          title: "Quality Assurance & Testing",
          description:
            "Comprehensive testing to ensure performance, security, and reliability.",
          image: imagePath.Img5,
        },
        {
          title: "Deployment & Continuous Optimization",
          description:
            "Smooth deployment with continuous monitoring, updates, and performance enhancements.",
          image: imagePath.Img6,
        },
      ],
    },
  },
  "real-estate": {
    title: "Real Estate",
    banner: {
      title: "Driving Digital Transformation in",
      highlight: "Real Estate",
      description:
        "We help real estate developers, brokers, property managers, and enterprises build secure, scalable, and intelligent digital solutions that streamline property operations, enhance customer experiences, improve sales efficiency, enable data-driven decisions, and support long-term growth in competitive real estate markets.",
      cta: "Book a Free Cons",
      backgroundImage: require("../../assets/images/real_estate.webp"),
    },

    services: {
      title: "End-to-End Real Estate Technology Services We Offer.",
      description:
        "Our real estate software development services empower agencies, developers, and property management companies to automate workflows, manage properties efficiently, enhance customer engagement, integrate digital channels, and deliver seamless, secure, and scalable real estate technology solutions.",
      data: [
        {
          number: "01",
          title: "Real Estate Management Software Development",
          subtitle: "Real Estate Management Software Development",
          description:
            "We develop comprehensive real estate management software that enables businesses to manage properties, listings, tenants, leases, contracts, and documentation efficiently. These solutions reduce manual effort, improve data accuracy, enhance operational visibility, and provide centralized control over residential and commercial real estate operations.",
          image: imagePath.Industries,
        },
        {
          number: "02",
          title: "Property Listing & Marketplace Platforms",
          subtitle: "Property Listing & Marketplace Platforms",
          description:
            "Our property listing and marketplace platforms allow real estate businesses to showcase properties, manage listings, handle inquiries, and connect buyers, sellers, and agents seamlessly. These solutions improve property visibility, enhance customer engagement, support advanced search features, and accelerate sales cycles.",
          image: imagePath.NextSection,
        },
        {
          number: "03",
          title: "Property & Facility Management Solutions",
          subtitle: "Property & Facility Management Solutions",
          description:
            "We build property and facility management systems that help organizations manage maintenance, inspections, work orders, vendors, and assets effectively. These platforms improve operational efficiency, reduce maintenance costs, ensure timely issue resolution, and provide real-time insights into property performance.",
          image: imagePath.Industries,
        },
        {
          number: "04",
          title: "Real Estate CRM & Sales Automation",
          subtitle: "Real Estate CRM & Sales Automation",
          description:
            "Our real estate CRM solutions help agents and brokers manage leads, customer interactions, follow-ups, and sales pipelines efficiently. By automating communication and tracking customer preferences, these systems improve conversion rates, strengthen relationships, and enhance overall sales productivity.",
          image: imagePath.NextSection,
        },
        {
          number: "05",
          title: "Real Estate Analytics & Reporting Solutions",
          subtitle: "Real Estate Analytics & Reporting Solutions",
          description:
            "We develop real estate analytics and reporting solutions that transform property, sales, and market data into actionable insights. These tools help businesses analyze trends, forecast demand, evaluate investments, optimize pricing strategies, and make informed, data-driven real estate decisions.",
          image: imagePath.Industries,
        },
      ],
    },

    stackItems: {
      title: "Tailored Real Estate Software Solutions",
      description:
        "Our real estate software solutions are tailored to meet the diverse needs of real estate businesses, ensuring scalability, data security, seamless integrations, operational efficiency, and long-term adaptability across residential, commercial, and enterprise real estate environments.",
      data: [
        {
          title: "Property & Operations Management",
          image: imagePath.Tail,
          points: [
            "Centralized management of properties and listings",
            "Real-time tracking of leases, tenants, and contracts",
            "Integration with third-party property and CRM systems",
            "Advanced reporting and operational analytics",
          ],
        },
        {
          title: "Security, Payments & Platform Scalability",
          image: imagePath.Tail,
          points: [
            "Secure user authentication and role-based access",
            "Encrypted data storage and transaction handling",
            "Scalable infrastructure for growing property portfolios",
            "Continuous monitoring and system performance optimization",
          ],
        },
      ],
    },

    readyToHire: {
      title: "Ready to Hire the Best Real Estate Software Developers",
      cta: "Hire our dev",
    },

    partnerReasons: {
      title: "Why Partner with Us",
      description:
        "We help real estate businesses embrace digital transformation by combining industry expertise, modern technologies, and proven development practices.",
      data: [
        {
          title: "Real Estate Domain Expertise",
          description:
            "Strong experience in real estate platforms, property management, and CRM solutions.",
        },
        {
          title: "Customer-Centric Digital Solutions",
          description:
            "Platforms designed to improve buyer, seller, and tenant experiences.",
        },
        {
          title: "Scalable & Reliable Systems",
          description:
            "Solutions built to support growing portfolios and high user demand.",
        },
        {
          title: "Dedicated Support & Maintenance",
          description:
            "Ongoing technical support, monitoring, and continuous enhancements.",
        },
      ],
    },

    process: {
      title: "Our Process for Real Estate Software Development",
      description:
        "A structured and results-driven development process focused on usability, scalability, security, and performance for real estate technology solutions.",
      data: [
        {
          title: "Real Estate Consultation & Strategy",
          description:
            "Understanding business goals, property workflows, and customer journeys to define the right digital strategy.",
          image: imagePath.Img1,
        },
        {
          title: "Requirement Analysis & Planning",
          description:
            "Detailed planning of features, integrations, scalability, and system architecture.",
          image: imagePath.Img2,
        },
        {
          title: "UI/UX Design",
          description:
            "Designing intuitive and engaging interfaces for agents, buyers, and administrators.",
          image: imagePath.Img3,
        },
        {
          title: "Development & Integration",
          description:
            "Building robust real estate platforms with secure third-party integrations.",
          image: imagePath.Img4,
        },
        {
          title: "Quality Assurance & Testing",
          description:
            "Comprehensive testing to ensure performance, security, and reliability.",
          image: imagePath.Img5,
        },
        {
          title: "Deployment & Continuous Optimization",
          description:
            "Smooth deployment with ongoing monitoring, updates, and performance improvements.",
          image: imagePath.Img6,
        },
      ],
    },
  },
  ecommerce: {
    title: "eCommerce",
    banner: {
      title: "Accelerating Digital Growth in",
      highlight: "eCommerce",
      description:
        "We help eCommerce businesses, retailers, and online brands build scalable, secure, and high-performing digital commerce solutions that enhance customer experiences, streamline operations, improve conversions, enable omnichannel selling, and support long-term growth in competitive online markets.",
      cta: "Book a Free Cons",
      backgroundImage: require("../../assets/images/ecommerce.webp"),
    },

    services: {
      title: "End-to-End eCommerce Development Services We Offer.",
      description:
        "Our eCommerce development services empower startups and enterprises to launch, scale, and optimize online stores by automating workflows, improving customer engagement, integrating payment and logistics systems, enhancing security, and delivering seamless shopping experiences across web and mobile platforms.",
      data: [
        {
          number: "01",
          title: "Custom eCommerce Platform Development",
          subtitle: "Custom eCommerce Platform Development",
          description:
            "We build custom eCommerce platforms tailored to unique business needs, enabling product management, order processing, inventory control, and customer engagement. Our solutions support scalable architecture, secure transactions, third-party integrations, advanced analytics, and seamless user experiences that drive conversions and revenue growth.",
          image: imagePath.Industries,
        },
        {
          number: "02",
          title: "Marketplace Development Solutions",
          subtitle: "Marketplace Development Solutions",
          description:
            "Our marketplace solutions enable businesses to create multi-vendor platforms with vendor onboarding, product listings, commission management, and order tracking. These systems improve scalability, expand product offerings, enhance customer choice, and provide centralized control over marketplace operations and performance.",
          image: imagePath.NextSection,
        },
        {
          number: "03",
          title: "eCommerce Mobile App Development",
          subtitle: "eCommerce Mobile App Development",
          description:
            "We develop feature-rich eCommerce mobile applications that provide customers with personalized shopping experiences, real-time notifications, secure payments, and smooth checkout processes. These apps increase customer engagement, improve retention, support omnichannel strategies, and boost overall sales performance.",
          image: imagePath.Industries,
        },
        {
          number: "04",
          title: "Payment Gateway & Checkout Integration",
          subtitle: "Payment Gateway & Checkout Integration",
          description:
            "Our payment gateway and checkout solutions ensure secure, fast, and flexible transaction processing. We integrate multiple payment methods, fraud prevention tools, and automated billing features to reduce cart abandonment, enhance trust, and deliver smooth and reliable checkout experiences.",
          image: imagePath.NextSection,
        },
        {
          number: "05",
          title: "eCommerce Analytics & Conversion Optimization",
          subtitle: "eCommerce Analytics & Conversion Optimization",
          description:
            "We build analytics and optimization solutions that track customer behavior, sales performance, and marketing effectiveness. By leveraging data-driven insights, businesses can improve conversion rates, personalize shopping journeys, optimize pricing strategies, and make informed decisions to increase revenue.",
          image: imagePath.Industries,
        },
      ],
    },

    stackItems: {
      title: "Tailored eCommerce Software Solutions",
      description:
        "Our eCommerce software solutions are tailored to meet the diverse needs of online retailers and digital brands, ensuring scalability, security, seamless integrations, performance optimization, and long-term adaptability across evolving digital commerce ecosystems.",
      data: [
        {
          title: "Storefront & Operations Management",
          image: imagePath.Tail,
          points: [
            "Centralized management of products, orders, and inventory",
            "Real-time order processing and fulfillment tracking",
            "Integration with logistics, ERP, and CRM systems",
            "Advanced reporting and sales analytics",
          ],
        },
        {
          title: "Security, Payments & Scalability",
          image: imagePath.Tail,
          points: [
            "Secure user authentication and access control",
            "Encrypted payment processing and fraud prevention",
            "Scalable infrastructure for high traffic and peak sales",
            "Continuous monitoring and performance optimization",
          ],
        },
      ],
    },

    readyToHire: {
      title: "Ready to Hire the Best eCommerce Developers",
      cta: "Hire our dev",
    },

    partnerReasons: {
      title: "Why Partner with Us",
      description:
        "We help eCommerce businesses succeed online by combining industry expertise, modern technologies, and proven development practices to deliver scalable and conversion-focused digital commerce solutions.",
      data: [
        {
          title: "eCommerce Domain Expertise",
          description:
            "Strong experience in building online stores, marketplaces, and digital commerce platforms.",
        },
        {
          title: "Conversion-Focused Development",
          description:
            "Solutions designed to improve user experience and increase sales performance.",
        },
        {
          title: "Scalable & High-Performance Platforms",
          description:
            "Systems built to handle rapid growth and high transaction volumes.",
        },
        {
          title: "Dedicated Support & Maintenance",
          description:
            "Ongoing technical support, monitoring, and continuous improvements.",
        },
      ],
    },

    process: {
      title: "Our Process for eCommerce Software Development",
      description:
        "A structured and results-driven development process focused on scalability, security, performance, and customer experience for eCommerce technology solutions.",
      data: [
        {
          title: "eCommerce Consultation & Strategy",
          description:
            "Understanding business goals, target customers, and sales models to define the right eCommerce strategy.",
          image: imagePath.Img1,
        },
        {
          title: "Requirement Analysis & Planning",
          description:
            "Detailed planning of features, integrations, scalability, and platform architecture.",
          image: imagePath.Img2,
        },
        {
          title: "UI/UX Design",
          description:
            "Designing intuitive, engaging, and conversion-optimized shopping experiences.",
          image: imagePath.Img3,
        },
        {
          title: "Development & Integration",
          description:
            "Building robust eCommerce platforms with secure third-party integrations.",
          image: imagePath.Img4,
        },
        {
          title: "Quality Assurance & Testing",
          description:
            "Comprehensive testing to ensure performance, security, and reliability.",
          image: imagePath.Img5,
        },
        {
          title: "Deployment & Continuous Optimization",
          description:
            "Smooth deployment with ongoing monitoring, updates, and performance enhancements.",
          image: imagePath.Img6,
        },
      ],
    },
  },
  "media-entertainment": {
    title: "Media & Entertainment",
    banner: {
      title: "Redefining Digital Innovation in",
      highlight: "Media & Entertainment",
      description:
        "We help media companies, broadcasters, content creators, and entertainment platforms build scalable, immersive, and data-driven digital solutions that enhance audience engagement, streamline content operations, support multi-platform distribution, and drive sustainable growth in the evolving digital entertainment landscape.",
      cta: "Book a Free Cons",
      backgroundImage: require("../../assets/images/media_entertainment.webp"),
    },

    services: {
      title: "End-to-End Media & Entertainment Technology Services We Offer.",
      description:
        "Our media and entertainment software development services enable organizations to manage content efficiently, improve audience engagement, monetize digital assets, ensure seamless streaming experiences, and deliver secure, high-performance platforms across web, mobile, and connected devices.",
      data: [
        {
          number: "01",
          title: "OTT & Video Streaming Platform Development",
          subtitle: "OTT & Video Streaming Platform Development",
          description:
            "We develop robust OTT and video streaming platforms that support on-demand and live streaming, content management, user subscriptions, and multi-device compatibility. Our solutions ensure high-quality playback, secure content delivery, scalability for large audiences, and personalized viewing experiences across digital platforms.",
          image: imagePath.Industries,
        },
        {
          number: "02",
          title: "Media Content Management Systems",
          subtitle: "Media Content Management Systems",
          description:
            "Our media content management systems help organizations store, organize, distribute, and monetize digital content efficiently. These platforms support metadata management, workflow automation, version control, collaboration, and seamless distribution across multiple channels and media formats.",
          image: imagePath.NextSection,
        },
        {
          number: "03",
          title: "Digital Publishing & News Platforms",
          subtitle: "Digital Publishing & News Platforms",
          description:
            "We build digital publishing and news platforms that enable media houses to deliver real-time content, manage subscriptions, and engage audiences effectively. These solutions support content personalization, analytics, advertising integration, and responsive design for consistent experiences across devices.",
          image: imagePath.Industries,
        },
        {
          number: "04",
          title: "Media Analytics & Audience Insights Solutions",
          subtitle: "Media Analytics & Audience Insights Solutions",
          description:
            "Our media analytics solutions transform audience behavior, content performance, and engagement data into actionable insights. With advanced dashboards and reporting tools, media organizations can optimize content strategies, improve targeting, increase retention, and maximize monetization opportunities.",
          image: imagePath.NextSection,
        },
        {
          number: "05",
          title: "Interactive Entertainment & Gaming Platforms",
          subtitle: "Interactive Entertainment & Gaming Platforms",
          description:
            "We develop interactive entertainment and gaming platforms that support immersive user experiences, real-time interactions, in-app purchases, and community engagement. These solutions are designed for performance, scalability, security, and seamless integration with modern entertainment ecosystems.",
          image: imagePath.Industries,
        },
      ],
    },

    stackItems: {
      title: "Tailored Media & Entertainment Software Solutions",
      description:
        "Our media and entertainment software solutions are tailored to meet the dynamic needs of content-driven businesses, ensuring scalability, high availability, seamless integrations, data security, and long-term adaptability across rapidly evolving digital media environments.",
      data: [
        {
          title: "Content & Platform Management",
          image: imagePath.Tail,
          points: [
            "Centralized content storage and management systems",
            "Real-time content publishing and distribution",
            "Integration with advertising and monetization platforms",
            "Advanced analytics and performance reporting",
          ],
        },
        {
          title: "Security, Streaming & Scalability",
          image: imagePath.Tail,
          points: [
            "Secure user authentication and access control",
            "Content protection using DRM and encryption",
            "Scalable infrastructure for high traffic and streaming loads",
            "Continuous monitoring and performance optimization",
          ],
        },
      ],
    },

    readyToHire: {
      title: "Ready to Hire the Best Media & Entertainment Software Developers",
      cta: "Hire our dev",
    },

    partnerReasons: {
      title: "Why Partner with Us",
      description:
        "We help media and entertainment businesses innovate faster by combining domain expertise, creative thinking, and modern technologies to deliver engaging and scalable digital experiences.",
      data: [
        {
          title: "Media & Entertainment Domain Expertise",
          description:
            "Strong experience in OTT platforms, digital publishing, and media technology solutions.",
        },
        {
          title: "Audience-Centric Digital Experiences",
          description:
            "Solutions designed to maximize engagement and viewer satisfaction.",
        },
        {
          title: "Scalable & High-Performance Platforms",
          description:
            "Systems built to support large audiences and real-time content delivery.",
        },
        {
          title: "Dedicated Support & Maintenance",
          description:
            "Ongoing technical support, monitoring, and continuous enhancements.",
        },
      ],
    },

    process: {
      title: "Our Process for Media & Entertainment Software Development",
      description:
        "A structured and innovation-driven development process focused on performance, scalability, security, and user engagement for media and entertainment technology solutions.",
      data: [
        {
          title: "Media Consultation & Strategy",
          description:
            "Understanding content goals, audience needs, and distribution models to define the right digital strategy.",
          image: imagePath.Img1,
        },
        {
          title: "Requirement Analysis & Planning",
          description:
            "Detailed planning of platform features, integrations, scalability, and monetization strategies.",
          image: imagePath.Img2,
        },
        {
          title: "UI/UX Design",
          description:
            "Designing immersive, intuitive, and engaging user experiences across devices.",
          image: imagePath.Img3,
        },
        {
          title: "Development & Integration",
          description:
            "Building robust media platforms with secure APIs and third-party integrations.",
          image: imagePath.Img4,
        },
        {
          title: "Quality Assurance & Testing",
          description:
            "Comprehensive testing to ensure streaming quality, performance, and security.",
          image: imagePath.Img5,
        },
        {
          title: "Deployment & Continuous Optimization",
          description:
            "Smooth deployment with ongoing monitoring, updates, and performance enhancements.",
          image: imagePath.Img6,
        },
      ],
    },
  },
  government: {
    title: "Government",
    banner: {
      title: "Driving Digital Transformation for",
      highlight: "Government",
      description:
        "We help government bodies, public sector organizations, and smart city initiatives adopt secure, scalable, and citizen-centric digital solutions that improve service delivery, enhance transparency, streamline operations, strengthen data security, and support long-term digital governance initiatives.",
      cta: "Book a Free Cons",
      backgroundImage: require("../../assets/images/government.webp"),
    },

    services: {
      title: "End-to-End Government Technology Services We Offer.",
      description:
        "Our government software development services support public sector organizations by modernizing legacy systems, automating administrative workflows, improving citizen engagement, ensuring regulatory compliance, enhancing data security, and delivering efficient, transparent, and scalable digital governance solutions.",
      data: [
        {
          number: "01",
          title: "eGovernance & Citizen Service Platforms",
          subtitle: "eGovernance & Citizen Service Platforms",
          description:
            "We develop eGovernance platforms that enable governments to deliver citizen services digitally, including applications, approvals, grievances, and information access. These solutions improve service accessibility, reduce paperwork, enhance transparency, streamline administrative processes, and create efficient, citizen-friendly digital governance systems.",
          image: imagePath.Industries,
        },
        {
          number: "02",
          title: "Public Sector Management Software",
          subtitle: "Public Sector Management Software",
          description:
            "Our public sector management solutions help government departments manage operations, workflows, assets, and resources efficiently. These systems improve inter-department coordination, enhance data accuracy, reduce operational overhead, and provide real-time visibility into public sector activities and performance.",
          image: imagePath.NextSection,
        },
        {
          number: "03",
          title: "Smart City Solutions Development",
          subtitle: "Smart City Solutions Development",
          description:
            "We build smart city solutions that integrate data from multiple urban systems such as transportation, utilities, public safety, and infrastructure. These platforms enable data-driven decision-making, improve city operations, enhance citizen safety, and support sustainable and connected urban development initiatives.",
          image: imagePath.Industries,
        },
        {
          number: "04",
          title: "Government Data Management & Analytics",
          subtitle: "Government Data Management & Analytics",
          description:
            "Our government data management and analytics solutions help agencies collect, process, and analyze large volumes of public data securely. By leveraging dashboards and analytics tools, governments can improve policy planning, enhance transparency, monitor performance, and make informed data-driven decisions.",
          image: imagePath.NextSection,
        },
        {
          number: "05",
          title: "Cybersecurity & Compliance Solutions",
          subtitle: "Cybersecurity & Compliance Solutions",
          description:
            "We develop robust cybersecurity and compliance solutions to protect sensitive government data and digital infrastructure. These systems include access controls, encryption, threat monitoring, and compliance frameworks to ensure data integrity, system resilience, and adherence to government security standards.",
          image: imagePath.Industries,
        },
      ],
    },

    stackItems: {
      title: "Tailored Government Software Solutions",
      description:
        "Our government software solutions are tailored to meet the complex needs of public sector organizations, ensuring scalability, security, interoperability, regulatory compliance, and long-term adaptability for evolving digital governance and public service requirements.",
      data: [
        {
          title: "Government Operations & Service Management",
          image: imagePath.Tail,
          points: [
            "Centralized management of government services and workflows",
            "Real-time tracking of applications, requests, and approvals",
            "Integration with legacy government systems and databases",
            "Advanced reporting and operational analytics",
          ],
        },
        {
          title: "Security, Compliance & Infrastructure",
          image: imagePath.Tail,
          points: [
            "Role-based access control and identity management",
            "Secure storage and protection of sensitive government data",
            "Compliance with government regulations and standards",
            "Continuous monitoring and infrastructure optimization",
          ],
        },
      ],
    },

    readyToHire: {
      title: "Ready to Hire the Best Government Software Developers",
      cta: "Hire our dev",
    },

    partnerReasons: {
      title: "Why Partner with Us",
      description:
        "We help government organizations achieve digital transformation by combining public sector expertise, secure technologies, and proven development practices.",
      data: [
        {
          title: "Public Sector Domain Expertise",
          description:
            "Strong experience in eGovernance, smart city, and government software solutions.",
        },
        {
          title: "Security & Compliance First",
          description:
            "Solutions designed to meet strict government security and regulatory requirements.",
        },
        {
          title: "Scalable & Interoperable Platforms",
          description:
            "Systems built to integrate seamlessly across departments and services.",
        },
        {
          title: "Reliable Support & Maintenance",
          description:
            "Ongoing technical support, monitoring, and continuous system enhancements.",
        },
      ],
    },

    process: {
      title: "Our Process for Government Software Development",
      description:
        "A structured and compliance-driven development process focused on security, scalability, transparency, and efficiency for government technology solutions.",
      data: [
        {
          title: "Government Consultation & Strategy",
          description:
            "Understanding public sector goals, citizen needs, and regulatory requirements to define the right digital strategy.",
          image: imagePath.Img1,
        },
        {
          title: "Requirement Analysis & Planning",
          description:
            "Detailed planning of features, integrations, compliance standards, and system architecture.",
          image: imagePath.Img2,
        },
        {
          title: "UI/UX Design",
          description:
            "Designing accessible, inclusive, and user-friendly interfaces for citizens and officials.",
          image: imagePath.Img3,
        },
        {
          title: "Development & System Integration",
          description:
            "Building secure government platforms with legacy system and third-party integrations.",
          image: imagePath.Img4,
        },
        {
          title: "Quality Assurance & Security Testing",
          description:
            "Comprehensive testing to ensure performance, security, accessibility, and compliance.",
          image: imagePath.Img5,
        },
        {
          title: "Deployment & Continuous Improvement",
          description:
            "Smooth deployment with continuous monitoring, updates, and optimization.",
          image: imagePath.Img6,
        },
      ],
    },
  },
  automotive: {
    title: "Automotive",
    banner: {
      title: "Accelerating Digital Innovation in",
      highlight: "Automotive",
      description:
        "We help automotive manufacturers, suppliers, mobility providers, and dealerships build intelligent, scalable, and connected digital solutions that enhance vehicle performance, optimize manufacturing processes, improve customer experiences, enable smart mobility, and support long-term transformation across the automotive ecosystem.",
      cta: "Book a Free Cons",
      backgroundImage: require("../../assets/images/automotive.webp"),
    },

    services: {
      title: "End-to-End Automotive Technology Services We Offer.",
      description:
        "Our automotive software development services support OEMs, Tier-1 suppliers, fleet operators, and mobility startups by automating operations, enabling connected vehicles, improving production efficiency, enhancing safety, and delivering secure, data-driven digital solutions across the automotive value chain.",
      data: [
        {
          number: "01",
          title: "Connected Vehicle & Telematics Solutions",
          subtitle: "Connected Vehicle & Telematics Solutions",
          description:
            "We develop connected vehicle and telematics solutions that enable real-time vehicle tracking, diagnostics, predictive maintenance, and remote monitoring. These platforms improve fleet efficiency, enhance driver safety, reduce downtime, support data-driven insights, and enable seamless connectivity between vehicles, infrastructure, and digital systems.",
          image: imagePath.Industries,
        },
        {
          number: "02",
          title: "Automotive Manufacturing Software Solutions",
          subtitle: "Automotive Manufacturing Software Solutions",
          description:
            "Our automotive manufacturing software helps organizations optimize production planning, inventory control, quality management, and shop-floor operations. These solutions improve operational efficiency, reduce errors, enhance supply chain coordination, and provide real-time visibility into manufacturing performance and output.",
          image: imagePath.NextSection,
        },
        {
          number: "03",
          title: "Fleet Management Software Development",
          subtitle: "Fleet Management Software Development",
          description:
            "We build fleet management platforms that enable businesses to manage vehicles, drivers, routes, fuel consumption, and maintenance schedules efficiently. By leveraging analytics and GPS technologies, these systems reduce operational costs, improve utilization, enhance safety compliance, and support smarter transportation decisions.",
          image: imagePath.Industries,
        },
        {
          number: "04",
          title: "Automotive ERP & Dealer Management Systems",
          subtitle: "Automotive ERP & Dealer Management Systems",
          description:
            "Our automotive ERP and dealer management systems streamline sales, inventory, service operations, customer relationships, and financial processes. These platforms improve dealer efficiency, enhance customer engagement, support data-driven sales strategies, and provide centralized control over automotive business operations.",
          image: imagePath.NextSection,
        },
        {
          number: "05",
          title: "Electric Vehicle & Smart Mobility Solutions",
          subtitle: "Electric Vehicle & Smart Mobility Solutions",
          description:
            "We develop electric vehicle and smart mobility solutions that support EV management, charging infrastructure integration, energy monitoring, and mobility platforms. These solutions enable sustainable transportation, optimize charging operations, enhance user experiences, and support the future of connected and autonomous mobility.",
          image: imagePath.Industries,
        },
      ],
    },

    stackItems: {
      title: "Tailored Automotive Software Solutions",
      description:
        "Our automotive software solutions are tailored to meet the evolving needs of automotive manufacturers and mobility providers, ensuring scalability, connectivity, data security, seamless integrations, and long-term adaptability in a rapidly changing automotive and mobility landscape.",
      data: [
        {
          title: "Vehicle, Fleet & Operations Management",
          image: imagePath.Tail,
          points: [
            "Real-time vehicle and fleet tracking capabilities",
            "Centralized management of automotive operations and data",
            "Integration with IoT devices and automotive systems",
            "Advanced analytics and performance reporting tools",
          ],
        },
        {
          title: "Security, Compliance & Platform Scalability",
          image: imagePath.Tail,
          points: [
            "Secure data transmission and storage mechanisms",
            "Role-based access control and authentication",
            "Compliance with automotive safety and data standards",
            "Scalable infrastructure for connected vehicle ecosystems",
          ],
        },
      ],
    },

    readyToHire: {
      title: "Ready to Hire the Best Automotive Software Developers",
      cta: "Hire our dev",
    },

    partnerReasons: {
      title: "Why Partner with Us",
      description:
        "We help automotive businesses drive innovation and efficiency by combining deep industry expertise, modern technologies, and proven development practices.",
      data: [
        {
          title: "Automotive Domain Expertise",
          description:
            "Strong experience across connected vehicles, manufacturing systems, and mobility platforms.",
        },
        {
          title: "Technology-Driven Innovation",
          description:
            "Solutions built using modern architectures, IoT, analytics, and cloud technologies.",
        },
        {
          title: "Scalable & Future-Ready Platforms",
          description:
            "Systems designed to support evolving mobility and automotive business models.",
        },
        {
          title: "Dedicated Support & Maintenance",
          description:
            "Ongoing technical support, monitoring, and continuous enhancements.",
        },
      ],
    },

    process: {
      title: "Our Process for Automotive Software Development",
      description:
        "A structured and innovation-focused development process designed to ensure scalability, security, performance, and reliability for automotive and mobility technology solutions.",
      data: [
        {
          title: "Automotive Consultation & Strategy",
          description:
            "Understanding automotive business goals, mobility challenges, and technical requirements to define the right digital strategy.",
          image: imagePath.Img1,
        },
        {
          title: "Requirement Analysis & Planning",
          description:
            "Detailed planning of features, integrations, scalability, and system architecture.",
          image: imagePath.Img2,
        },
        {
          title: "UI/UX Design",
          description:
            "Designing intuitive and user-friendly interfaces for drivers, operators, and administrators.",
          image: imagePath.Img3,
        },
        {
          title: "Development & Integration",
          description:
            "Building robust automotive platforms with secure integrations and real-time capabilities.",
          image: imagePath.Img4,
        },
        {
          title: "Quality Assurance & Testing",
          description:
            "Comprehensive testing to ensure performance, safety, security, and reliability.",
          image: imagePath.Img5,
        },
        {
          title: "Deployment & Continuous Optimization",
          description:
            "Smooth deployment with continuous monitoring, updates, and performance improvements.",
          image: imagePath.Img6,
        },
      ],
    },
  },
  fintech: {
    title: "Fintech",
    banner: {
      title: "Transforming Innovation in",
      highlight: "Fintech",
      description:
        "We help fintech startups, digital banks, payment providers, and financial innovators build secure, scalable, and intelligent technology solutions that redefine financial services, improve user experiences, ensure regulatory compliance, and enable rapid growth in the fast-evolving fintech ecosystem.",
      cta: "Book a Free Cons",
      backgroundImage: require("../../assets/images/fintech.webp"),
    },

    services: {
      title: "End-to-End Fintech Development Services We Offer.",
      description:
        "Our fintech software development services empower companies to launch and scale digital financial products by automating financial workflows, enhancing security, ensuring compliance, improving customer engagement, and delivering reliable, high-performance platforms across modern financial ecosystems.",
      data: [
        {
          number: "01",
          title: "Digital Payments & Wallet Solutions",
          subtitle: "Digital Payments & Wallet Solutions",
          description:
            "We develop secure digital payment and wallet solutions that support online payments, mobile transactions, peer-to-peer transfers, and recurring billing. Our platforms include encryption, fraud prevention, multi-currency support, regulatory compliance, and seamless integrations to ensure fast, reliable, and user-friendly payment experiences.",
          image: imagePath.Industries,
        },
        {
          number: "02",
          title: "Neobanking & Digital Banking Platforms",
          subtitle: "Neobanking & Digital Banking Platforms",
          description:
            "Our neobanking and digital banking solutions provide customers with seamless access to accounts, transfers, cards, and financial services through web and mobile platforms. These systems enhance customer engagement, reduce operational costs, improve scalability, and support fully digital banking experiences.",
          image: imagePath.NextSection,
        },
        {
          number: "03",
          title: "Lending & Credit Management Software",
          subtitle: "Lending & Credit Management Software",
          description:
            "We build lending and credit management platforms that automate loan origination, credit scoring, underwriting, repayment tracking, and collections. By leveraging data analytics and automation, these solutions reduce risk, improve approval speed, enhance transparency, and support smarter lending decisions.",
          image: imagePath.Industries,
        },
        {
          number: "04",
          title: "Blockchain & Cryptocurrency Solutions",
          subtitle: "Blockchain & Cryptocurrency Solutions",
          description:
            "Our blockchain and cryptocurrency solutions enable secure digital asset management, smart contracts, decentralized applications, and crypto trading platforms. These systems enhance transparency, reduce transaction costs, improve security, and support innovative financial models within the fintech ecosystem.",
          image: imagePath.NextSection,
        },
        {
          number: "05",
          title: "Fintech Analytics, Risk & Compliance Solutions",
          subtitle: "Fintech Analytics, Risk & Compliance Solutions",
          description:
            "We develop fintech analytics, risk management, and compliance solutions that help organizations manage AML, KYC, fraud detection, and regulatory reporting. These platforms leverage real-time data insights to reduce financial risks, ensure compliance, and support informed, data-driven decision-making.",
          image: imagePath.Industries,
        },
      ],
    },

    stackItems: {
      title: "Tailored Fintech Software Solutions",
      description:
        "Our fintech software solutions are tailored to meet the dynamic needs of financial innovators, ensuring scalability, high availability, data security, regulatory compliance, and long-term adaptability across rapidly evolving digital financial ecosystems.",
      data: [
        {
          title: "Financial Data & Transaction Management",
          image: imagePath.Tail,
          points: [
            "Real-time processing of digital financial transactions",
            "Secure storage of sensitive financial and user data",
            "High-performance systems for large transaction volumes",
            "Advanced analytics and financial reporting capabilities",
          ],
        },
        {
          title: "Security, Compliance & Platform Scalability",
          image: imagePath.Tail,
          points: [
            "AML and KYC compliance implementation",
            "End-to-end encryption and fraud prevention mechanisms",
            "Role-based access control and authentication",
            "Scalable infrastructure for high-growth fintech platforms",
          ],
        },
      ],
    },

    readyToHire: {
      title: "Ready to Hire the Best Fintech Software Developers",
      cta: "Hire our dev",
    },

    partnerReasons: {
      title: "Why Partner with Us",
      description:
        "We help fintech businesses innovate faster and scale securely by combining financial expertise, modern technologies, and proven development practices.",
      data: [
        {
          title: "Fintech Domain Expertise",
          description:
            "Strong experience across payments, digital banking, lending, and blockchain solutions.",
        },
        {
          title: "Security & Compliance First",
          description:
            "Solutions built to meet strict financial regulations and security standards.",
        },
        {
          title: "Scalable & Future-Ready Architecture",
          description:
            "Platforms designed to support rapid growth and evolving fintech models.",
        },
        {
          title: "Dedicated Support & Maintenance",
          description:
            "Ongoing technical support, monitoring, and continuous improvements.",
        },
      ],
    },

    process: {
      title: "Our Process for Fintech Software Development",
      description:
        "A structured and security-driven development process focused on scalability, compliance, performance, and innovation for fintech technology solutions.",
      data: [
        {
          title: "Fintech Consultation & Strategy",
          description:
            "Understanding fintech business models, regulatory requirements, and user needs to define the right digital strategy.",
          image: imagePath.Img1,
        },
        {
          title: "Requirement Analysis & Planning",
          description:
            "Detailed planning of features, integrations, scalability, and compliance needs.",
          image: imagePath.Img2,
        },
        {
          title: "UI/UX Design",
          description:
            "Designing intuitive, secure, and user-friendly fintech experiences.",
          image: imagePath.Img3,
        },
        {
          title: "Development & Integration",
          description:
            "Building robust fintech platforms with secure APIs and third-party integrations.",
          image: imagePath.Img4,
        },
        {
          title: "Quality Assurance & Security Testing",
          description:
            "Comprehensive testing to ensure performance, data security, and regulatory compliance.",
          image: imagePath.Img5,
        },
        {
          title: "Deployment & Continuous Optimization",
          description:
            "Smooth deployment with continuous monitoring, updates, and performance enhancements.",
          image: imagePath.Img6,
        },
      ],
    },
  },
};
